import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './CheckinDetails.css';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';
import { fireAudit } from '../utils/auditClient';

const CheckinDetails = ({ userId: propUserId, name: propName }) => {
  const location = useLocation();
  const locationState = location.state || {};

  const userId = propUserId || locationState.userId;
  const name = propName || locationState.name;

  const { selectedCenter, userData } = useUser();
  const loginId = userData?.userId;
  const token = localStorage.getItem('auth-token');
  const [checkinData, setCheckinData] = useState({
    name: '',
    academyId: selectedCenter || '',
    playBookId: [],
    date: new Date().toISOString().split('T')[0],
    checkinTime: new Date().toLocaleTimeString(),
    message: '',
  });

  const [errorMessage, setErrorMessage] = useState('');
  const baselineRef = useRef(null);

  useEffect(() => {
    const fetchCheckinDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_USER_DETAILS}?academy_id=${selectedCenter}&user_id=${userId}&login_user_id=${loginId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
  
        const data = await response.json();
  
        if (data.success && data.data.checkInForm) {
          const checkInForm = data.data.checkInForm;
  
          setCheckinData({
            name: checkInForm.name || '',
            userId: checkInForm.userId || userId || '',
            batchId: checkInForm.batchId || null,
            weekDays: checkInForm.weekDays || '',
            displayTime: checkInForm.displayTime || '',
            currentDate: checkInForm.currentDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            checkinTime: checkInForm.currentTime || new Date().toLocaleTimeString(),
            message: checkInForm.message || '',
            is_Comp: checkInForm.is_Comp || false,
            playBookId: checkInForm.playBookId || [],
            warningType: checkInForm.warningCategoryType || '',
            paymentDueDate: checkInForm.paymentDueDate || null,
            academyId: checkInForm.academyId || selectedCenter || '',
            compBatchId: checkInForm.compBatchId || null,
          });
  
          baselineRef.current = {
            ...checkInForm,
            currentDate: new Date(new Date().toISOString().split('T')[0] + 'T00:00:00.000Z').toISOString(),
          };
        } else {
          setCheckinData((prev) => ({
            ...prev,
            name: name || '',
            userId: userId || '',
            academyId: selectedCenter || '',
            message: "Looks like you haven't booked any slot today. Please contact the center manager.",
          }));
        }
      } catch {
        setErrorMessage('Error fetching check-in details');
      }
    };
  
    if (userId) {
      fetchCheckinDetails();
    }
  }, [userId]);
  

  const handleMarkAttendance = async () => {
    const requestData = {
      name: checkinData.name || '',
      userId: checkinData.userId || '',
      batchId: checkinData.batchId || null,
      weekDays: checkinData.weekDays || '',
      displayTime: checkinData.displayTime || '',
      currentDate: new Date(new Date().toISOString().split('T')[0] + 'T00:00:00.000Z').toISOString(),
      message: checkinData.message || '',
      is_Comp: checkinData.is_Comp || false,
      playBookId: checkinData.playBookId || [],
      currentTime: checkinData.checkinTime || new Date().toLocaleTimeString(),
      warningCategoryType: checkinData.warningType || '',
      paymentDueDate: checkinData.paymentDueDate || null,
      academyId: checkinData.academyId || '',
      compBatchId: checkinData.compBatchId || null,
    };
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_MARK_ATTENDANCE}?userId=${loginId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
  
      const result = await response.json();
  
      Swal.fire({
        icon: result.success ? 'success' : 'error',
        title: result.success ? 'Success' : 'Failed',
        text: result.success ? 'Attendance marked successfully!' : 'Failed to mark attendance.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
  
      if (result.success) {
        const oldPayload = baselineRef.current || {};
        const newPayload = requestData;
        const affected_id =
          result?.data?.id ||
          `${requestData.userId}-${requestData.academyId}-${requestData.currentDate}`;
        fireAudit({
          entity: 'attendance',
          affected_id,
          action: 'create',
          section: 'Attendance',
          subSection: 'CheckinDetails',
          businessApi: `${process.env.REACT_APP_API_MARK_ATTENDANCE}?userId=${userId}`,
          businessMethod: 'POST',
          oldPayload,
          newPayload,
          extra: { page: 'CheckinDetails' },
        });
        baselineRef.current = newPayload;
      }
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error marking attendance.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };
  

  return (
    <div className="checkin-container-nd">
      <div className="checkin-form-nd">
        {errorMessage ? (
          <p className="checkin-error-msg-nd">{errorMessage}</p>
        ) : (
          <>
            <div className="checkin-group-nd">
              <label htmlFor="name" className="checkin-label-nd">Name:</label>
              <input type="text" id="name" value={checkinData.name} disabled className="checkin-input-nd" />
            </div>

            <div className="checkin-group-nd">
              <label htmlFor="date" className="checkin-label-nd">Date:</label>
              <input type="date" id="date" value={checkinData.date} disabled className="checkin-input-nd" />
            </div>

            <div className="checkin-group-nd">
              <label htmlFor="checkinTime" className="checkin-label-nd">Check-in Time:</label>
              <input type="text" id="checkinTime" value={checkinData.checkinTime} disabled className="checkin-input-nd" />
            </div>

            {checkinData.message?.trim() && (
              <div className="checkin-group-nd">
                <label htmlFor="message" className="checkin-label-nd">Message:</label>
                <textarea id="message" value={checkinData.message} disabled className="checkin-input-nd" />
              </div>
            )}

            {checkinData.warningType === 'NORMAL' && (
              <button
                className="checkin-btn-nd"
                onClick={handleMarkAttendance}
              >
                Mark Attendance
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CheckinDetails;
